// Codec analysis for AAC-LC and the AAC-LC core of HE-AAC (v1 and v2), in ADTS
// and MP4. The bitstream work is in aacdec.ts; this file turns the parsed data
// into lanes, inspector views, spectrogram columns and file notes.
import type { ParsedFile } from '../parsers/types';
import {
  CCE, CPE, DSE, EIGHT_SHORT, ELEMENT_NAMES, END, FIL, INTENSITY_HCB, INTENSITY_HCB2, LFE, NOISE_HCB, PCE, SAMPLE_RATES, SCE, ZERO_HCB,
  parseRawBlock, rateIndex, spectra,
} from './aacdec';
import type { ChannelSpectrum, Element, Ics, RawBlock } from './aacdec';
import type { BarsView, CodecAnalysis, CodecLane, CodecView, SpectrumColumn, SpectrumView, TextView } from './types';

/** Scales coefficients so that a full-scale sine peaks near 1.0 (long windows; short windows ×8). */
const NORM_LONG = 1 / (32768 * 1024);
const NORM_SHORT = NORM_LONG * 8;

const kHz = (hz: number) => `${+(hz / 1000).toFixed(1)} kHz`;
const num = (n: number) => n.toLocaleString('en-US');
const pct = (x: number) => `${Math.round(x * 100)}%`;

const WINDOW_NAMES = ['Only long', 'Long start', 'Eight short', 'Long stop'];

const WINDOW_LANE: CodecLane = {
  name: 'Window',
  help: 'Long windows (2,048 samples) give fine frequency detail. At a sudden attack, a long window spreads coding noise to the time before the attack, where you can hear it (pre-echo). So the encoder switches to eight short windows there.',
  values: [
    { value: 'Only long', help: 'One long window with 1,024 frequency lines. Best for steady sound.' },
    { value: 'Long start', help: 'A long window with a steep end. It prepares the change to short windows in the next frame.' },
    { value: 'Eight short', help: 'Eight short windows with 128 lines each. They keep the coding noise close to a sudden attack.' },
    { value: 'Long stop', help: 'A long window with a steep start. It returns from short windows to long ones.' },
  ],
};

const STEREO_VALUES = [
  { value: 'Mono', help: 'One channel only, in a single channel element.' },
  { value: 'Left/right', help: 'Left and right are stored separately in every band.' },
  { value: 'M/S some bands', help: 'Some bands store mid (L+R) and side (L−R). The other bands store left and right.' },
  { value: 'M/S all bands', help: 'Every band stores mid (L+R) and side (L−R). This is cheap when the two channels are almost the same.' },
  { value: 'Intensity', help: 'Some high bands store no right channel at all, only its level relative to the left.' },
  { value: 'Parametric', help: 'The core holds one mono channel. Parametric Stereo hints in the SBR data rebuild left and right.' },
];

const NOISE_LANE: CodecLane = {
  name: 'Noise',
  help: 'Perceptual Noise Substitution (PNS). For a noisy band, such as cymbals or breath, the encoder sends only the energy. The decoder fills the band with new random noise. This costs a few bits instead of hundreds.',
  values: [
    { value: 'No PNS', help: 'No band in this frame is replaced by noise.' },
    { value: 'PNS', help: 'At least one band in this frame is sent as a noise energy only.' },
  ],
};

const CODEBOOK_TEXT: Record<number, string> = {
  1: 'values −1…1, 4 at a time', 2: 'values −1…1, 4 at a time', 3: 'values 0…2 + sign, 4 at a time', 4: 'values 0…2 + sign, 4 at a time',
  5: 'values −4…4, in pairs', 6: 'values −4…4, in pairs', 7: 'values 0…7 + sign, in pairs', 8: 'values 0…7 + sign, in pairs',
  9: 'values 0…12 + sign, in pairs', 10: 'values 0…12 + sign, in pairs', 11: 'values 0…15 + escape, in pairs',
};

const HOW_TEXT: TextView = {
  kind: 'text',
  title: 'How AAC stores sound',
  body: [
    '**Frequencies, not samples.** AAC uses the MDCT to turn each block of samples into frequency lines: 1,024 per frame. Music puts most of its energy in a few lines, so most lines are small or zero and cost almost nothing. Overlapped windows let the blocks join without clicks.',
    '**Scalefactors.** Lines are grouped into scalefactor bands: narrow at low frequencies, wide at high ones, as in the ear. Each band has one scalefactor, which sets its step size. A big step gives coarse values and few bits. The encoder picks each step so the rounding noise stays below what you can hear.',
    '**Codebooks.** Bands form sections, and each section picks one of 11 Huffman codebooks. Quiet sections use a book for small values, loud sections a book for large ones. Codebook 11 has an escape for values of 16 and more. A good match saves bits. A zero section costs no bits at all.',
    '**Noise and stereo tricks.** PNS sends only the energy of a noisy band. Mid/side and intensity stereo remove what the two channels share.',
  ].join('\n\n'),
};

interface Ctx {
  pf: ParsedFile;
  bytes: Uint8Array;
  sfi: number;
  coreRate: number;
  he: boolean;
  v2: boolean;
  adts: boolean;
}

interface FrameData {
  blocks: RawBlock[];
  /** Byte size of each block. */
  sizes: number[];
}

interface FullFrame extends FrameData {
  chans: ChannelSpectrum[][];
}

function parseFrame(ctx: Ctx, i: number): FrameData {
  const { bytes: b } = ctx;
  const f = ctx.pf.frames[i];
  if (!ctx.adts) {
    const blk = parseRawBlock(b, f.start, f.end, ctx.sfi);
    checkEnd(blk, f.end);
    return { blocks: [blk], sizes: [f.end - f.start] };
  }
  const p = f.start;
  const crc = !(b[p + 1] & 1);
  const n = (b[p + 6] & 3) + 1;
  let pos = p + 7 + (crc ? 2 * n : 0);
  const blocks: RawBlock[] = [], sizes: number[] = [];
  for (let k = 0; k < n; k++) {
    const blk = parseRawBlock(b, pos, f.end, ctx.sfi);
    blocks.push(blk);
    const next = ((blk.endBit + 7) >> 3) + (crc && n > 1 ? 2 : 0);
    sizes.push(next - pos);
    pos = next;
  }
  if (pos !== f.end) throw new Error(`The last raw data block ends at byte ${num(pos - f.start)}, but the frame is ${num(f.end - f.start)} bytes long.`);
  return { blocks, sizes };
}

function checkEnd(blk: RawBlock, end: number): void {
  const e = (blk.endBit + 7) >> 3;
  if (e !== end) throw new Error(`The END element ends at byte ${num(e)}, but the frame ends at byte ${num(end)}. Some data was not read as expected.`);
}

/** Channel names in element order: SCE (center or mono), CPE pairs, LFE. */
function channelNames(els: Element[], v2: boolean): string[] {
  const sces = els.filter((e) => e.id === SCE).length;
  const cpes = els.filter((e) => e.id === CPE).length;
  const out: string[] = [];
  let cpeIdx = 0, sceIdx = 0;
  const pairs = [['Left', 'Right'], ['Surround left', 'Surround right'], ['Rear left', 'Rear right']];
  for (const e of els) {
    if (e.id === SCE) {
      out.push(cpes === 0 ? (sces === 1 ? (v2 ? 'Mono core' : 'Mono') : `Channel ${sceIdx + 1}`) : sceIdx === 0 ? 'Center' : `Center ${sceIdx + 1}`);
      sceIdx++;
    } else if (e.id === CPE) {
      out.push(...(pairs[cpeIdx] ?? [`Pair ${cpeIdx + 1} left`, `Pair ${cpeIdx + 1} right`]));
      cpeIdx++;
    } else if (e.id === LFE) out.push('LFE');
  }
  return out;
}

function hasNoise(ics: Ics): boolean {
  for (const t of ics.bandType) if (t === NOISE_HCB) return true;
  return false;
}
function countType(ics: Ics, pred: (t: number) => boolean): number {
  let n = 0;
  for (const t of ics.bandType) if (pred(t)) n++;
  return n;
}
const isIntensity = (t: number) => t === INTENSITY_HCB || t === INTENSITY_HCB2;

/** Stereo decision for a block (index into STEREO_VALUES). */
function stereoOf(blk: RawBlock, v2: boolean): number {
  const cpe = blk.elements.find((e) => e.id === CPE);
  if (!cpe) return blk.elements.some((e) => e.id === SCE) ? (v2 ? 5 : 0) : -1;
  if (cpe.ics[1] && countType(cpe.ics[1], isIntensity)) return 4;
  if (!cpe.commonWindow || !cpe.msMask) return 1;
  if (cpe.msMask === 2) return 3;
  const used = cpe.msUsed!;
  let n = 0;
  for (const u of used) n += u;
  return n === 0 ? 1 : n === used.length ? 3 : 2;
}

/** Highest coded line (exclusive), as a fraction of the band 0…Nyquist; 0 when nothing is coded. */
function topFraction(ics: Ics): number {
  const { info, q } = ics;
  let top = 0;
  for (let w = 0; w < info.numWindows; w++) {
    const len = 1024 / info.numWindows;
    for (let k = len - 1; k >= 0; k--) {
      if (q[w * len + k]) {
        top = Math.max(top, (k + 1) / len);
        break;
      }
    }
  }
  return top;
}

/** Top of the highest noise (PNS) or intensity band, as a fraction of 0…Nyquist. */
function paramTopFraction(ics: Ics): number {
  const { info, bandType } = ics;
  const M = info.maxSfb, len = 1024 / info.numWindows;
  let top = 0;
  for (let i = 0; i < bandType.length; i++) {
    const t = bandType[i];
    if (t === NOISE_HCB || isIntensity(t)) top = Math.max(top, info.swb[(i % M) + 1] / len);
  }
  return top;
}

function zeroCount(ics: Ics): number {
  let z = 0;
  for (const v of ics.q) if (!v) z++;
  return z;
}

function msCount(el: Element): [number, number] {
  if (!el.msUsed || !el.msMask) return [0, el.ics[0].info.groups.length * el.ics[0].info.maxSfb];
  let n = 0;
  for (const u of el.msUsed) n += u;
  return [n, el.msUsed.length];
}

function extName(t: number | undefined): string {
  switch (t) {
    case 13: case 14: return 'SBR';
    case 11: return 'Dynamic range';
    case 12: return 'MPEG Surround';
    case 1: return 'Fill data';
    case 2: return 'Data element';
    default: return 'Fill';
  }
}

function sbrBytes(blk: RawBlock): number {
  let n = 0;
  for (const e of blk.elements) if (e.id === FIL && (e.extType === 13 || e.extType === 14)) n += e.bytes ?? 0;
  return n;
}

export function analyze(pf: ParsedFile, bytes: Uint8Array): CodecAnalysis | null {
  const codec = pf.audio.codec;
  const adts = pf.format === 'aac';
  if (adts ? codec !== 'AAC-LC' : pf.format !== 'mp4' || !['AAC-LC', 'HE-AAC', 'HE-AAC v2'].includes(codec)) return null;
  if (!pf.frames.length || !pf.audio.sampleRate) return null;
  let he = codec.startsWith('HE-AAC');
  const v2 = codec === 'HE-AAC v2';
  let sfi: number;
  if (adts) {
    sfi = (bytes[pf.frames[0].start + 2] >> 2) & 15;
    if (sfi > 12) return null;
  } else sfi = rateIndex(he ? pf.audio.sampleRate / 2 : pf.audio.sampleRate);
  const coreRate = adts ? SAMPLE_RATES[sfi] : he ? pf.audio.sampleRate / 2 : pf.audio.sampleRate;
  const ctx: Ctx = { pf, bytes, sfi, coreRate, he, v2, adts };
  const nyquist = coreRate / 2;
  const N = pf.frames.length;

  // Probe a spread of frames: does the file use PNS, and (for ADTS) implicit SBR?
  let pnsSeen = false;
  const probeStep = Math.max(1, Math.floor(N / 200));
  for (let i = 0; i < N; i += probeStep) {
    try {
      const fd = parseFrame(ctx, i);
      for (const blk of fd.blocks) {
        for (const e of blk.elements) {
          if (e.ics.some(hasNoise)) pnsSeen = true;
          if (e.id === FIL && (e.extType === 13 || e.extType === 14)) he = ctx.he = true;
        }
      }
    } catch {
      /* damaged frames show up later, in detail() */
    }
  }

  const stereoValues = v2 ? STEREO_VALUES : STEREO_VALUES.slice(0, 5);
  const lanes: CodecLane[] = [
    WINDOW_LANE,
    {
      name: 'Stereo',
      help: 'How the encoder stores a pair of channels. When left and right are alike, it is cheaper to store their sum (mid) and difference (side). Intensity stereo goes further: for high bands it stores one channel plus a level.',
      values: stereoValues,
    },
  ];
  if (pnsSeen) lanes.push(NOISE_LANE);

  // Small cache so detail() and columns() of the same frame share one parse.
  const cache = new Map<number, FullFrame>();
  const full = (i: number): FullFrame => {
    let r = cache.get(i);
    if (!r) {
      const fd = parseFrame(ctx, i);
      r = { ...fd, chans: fd.blocks.map((b) => spectra(b)) };
      if (cache.size >= 8) cache.delete(cache.keys().next().value!);
      cache.set(i, r);
    }
    return r;
  };

  const decisionCache = new Int8Array(N * 3).fill(-2);
  const decisions = (i: number): number[] => {
    if (decisionCache[i * 3] !== -2) return [decisionCache[i * 3], decisionCache[i * 3 + 1], decisionCache[i * 3 + 2]].slice(0, lanes.length);
    let out = [-1, -1, -1];
    try {
      const blk = parseFrame(ctx, i).blocks[0];
      const main = blk.elements.find((e) => e.id === SCE || e.id === CPE);
      out = [main ? main.ics[0].info.seq : -1, stereoOf(blk, v2), blk.elements.some((e) => e.ics.some(hasNoise)) ? 1 : 0];
    } catch {
      /* unknown */
    }
    decisionCache.set(out, i * 3);
    return out.slice(0, lanes.length);
  };

  const detail = (i: number): CodecView[] => {
    const fr = full(i);
    const views: CodecView[] = [];
    const multi = fr.blocks.length > 1;
    fr.blocks.forEach((blk, bi) => {
      const chans = fr.chans[bi];
      const prefix = multi ? `Block ${bi + 1} · ` : '';
      views.push(frameSummary(blk, chans, fr.sizes[bi], nyquist, prefix));
      if (he) views.push(heText(blk, fr.sizes[bi], coreRate, v2));
      const names = channelNames(blk.elements, v2);
      chans.forEach((c, ci) => views.push(...channelViews(c, `${prefix}${names[ci] ?? `Channel ${ci + 1}`}`, nyquist)));
      views.push(...bitViews(blk, fr.sizes[bi], prefix));
    });
    views.push(HOW_TEXT);
    return views;
  };

  const columns = (i: number): SpectrumColumn[] => {
    const fr = full(i);
    const f = pf.frames[i];
    const [t0, t1] = f.time ?? [0, 0];
    const out: SpectrumColumn[] = [];
    const nb = fr.blocks.length;
    fr.chans.forEach((chans, bi) => {
      const b0 = t0 + ((t1 - t0) * bi) / nb, b1 = t0 + ((t1 - t0) * (bi + 1)) / nb;
      out.push(...blockColumns(chans.filter((c) => c.element.id !== LFE), b0, b1, nyquist));
    });
    return out;
  };

  const summarize = () => fileSummary(ctx, N, nyquist, pnsSeen);

  return {
    codec: he ? (v2 ? 'HE-AAC v2 (AAC-LC core)' : 'HE-AAC (AAC-LC core)') : 'AAC-LC',
    lanes,
    decisions,
    detail,
    columns,
    summarize,
  };
}

// ---------------------------------------------------------------------------
// Views

function frameSummary(blk: RawBlock, chans: ChannelSpectrum[], bytes: number, nyquist: number, prefix: string): TextView {
  const main = chans.find((c) => c.element.id !== LFE) ?? chans[0];
  const parts: string[] = [];
  if (main) {
    const seq = main.ics.info.seq;
    parts.push(
      seq === EIGHT_SHORT
        ? `Eight short windows in ${main.ics.info.groups.length === 1 ? 'one group' : `${main.ics.info.groups.length} groups`}: the encoder saw a sudden change.`
        : `${WINDOW_NAMES[seq]} window.`,
    );
  }
  const cpe = blk.elements.find((e) => e.id === CPE);
  if (cpe) {
    const [n, total] = msCount(cpe);
    if (!cpe.commonWindow) parts.push('The two channels use different windows, so they are stored as left and right.');
    else if (n === 0) parts.push('Left and right are stored separately.');
    else if (n === total) parts.push('Mid/side stereo in every band.');
    else parts.push(`Mid/side stereo in ${n} of ${total} bands.`);
    const is = countType(cpe.ics[1], isIntensity);
    if (is) parts.push(`Intensity stereo in ${is} ${is === 1 ? 'band' : 'bands'}: the right channel copies the left there.`);
  }
  let noise = 0, zeros = 0, lines = 0, top = 0, ptop = 0, tns = 0;
  for (const c of chans) {
    if (c.element.id === LFE) continue;
    noise += countType(c.ics, (t) => t === NOISE_HCB);
    zeros += zeroCount(c.ics);
    lines += 1024;
    top = Math.max(top, topFraction(c.ics));
    ptop = Math.max(ptop, paramTopFraction(c.ics));
    if (c.ics.tns) tns++;
  }
  if (noise) parts.push(`PNS replaces ${noise} ${noise === 1 ? 'band' : 'bands'} with noise.`);
  if (tns) parts.push('TNS shapes the coding noise in time inside the frame.');
  if (lines) {
    if (top === 0 && ptop === 0) parts.push('No line holds a value: this frame is silent.');
    else if (ptop > top) parts.push(`Lines are coded up to ${kHz(top * nyquist)}. Above that, up to ${kHz(ptop * nyquist)}, bands hold only noise or intensity levels. ${pct(zeros / lines)} of the lines are zero.`);
    else parts.push(`Nothing is coded above ${kHz(top * nyquist)}. ${pct(zeros / lines)} of the lines are zero.`);
  }
  parts.push(`The frame uses ${num(bytes)} bytes (${num(bytes * 8)} bits).`);
  return { kind: 'text', title: `${prefix}This frame`, body: parts.join(' ') };
}

function heText(blk: RawBlock, bytes: number, coreRate: number, v2: boolean): TextView {
  const sbr = sbrBytes(blk);
  const body = [
    `This is HE-AAC. The AAC core runs at ${kHz(coreRate)}, so it codes only sound up to ${kHz(coreRate / 2)}: the lower half of the output spectrum. The spectra here show only this core.`,
    sbr
      ? `**Spectral Band Replication (SBR)** rebuilds the highs. Its data is in a fill element: ${num(sbr)} bytes in this frame (${pct(sbr / bytes)} of it). The decoder copies the low bands up and shapes them with the SBR envelope. The output runs at ${kHz(coreRate * 2)}.`
      : '**Spectral Band Replication (SBR)** rebuilds the highs, but this frame holds no SBR data.',
  ];
  if (v2) body.push('**Parametric Stereo** is on too. The core is one mono channel. A few bytes of stereo hints inside the SBR data rebuild left and right.');
  return { kind: 'text', title: 'Only half the spectrum is coded', body: body.join('\n\n') };
}

function bandLabel(cb: number, sf: number): string {
  if (cb === ZERO_HCB) return 'zero';
  if (cb === NOISE_HCB) return `noise ${sf}`;
  if (isIntensity(cb)) return `IS ${sf}`;
  return `sf ${sf}`;
}

function regionOf(cb: number): { label: string; kind: 'coded' | 'small' | 'zero' | 'noise' | 'intensity' } {
  if (cb === ZERO_HCB) return { label: 'Zero (no bits)', kind: 'zero' };
  if (cb === NOISE_HCB) return { label: 'Noise (PNS)', kind: 'noise' };
  if (cb === INTENSITY_HCB) return { label: 'Intensity (in phase)', kind: 'intensity' };
  if (cb === INTENSITY_HCB2) return { label: 'Intensity (out of phase)', kind: 'intensity' };
  return { label: `Codebook ${cb}: ${CODEBOOK_TEXT[cb]}`, kind: cb <= 2 ? 'small' : 'coded' };
}

function channelViews(c: ChannelSpectrum, name: string, nyquist: number): SpectrumView[] {
  const { ics, coef } = c;
  const { info } = ics;
  const M = info.maxSfb;
  const icsBits = ics.marks[ics.marks.length - 1] - ics.marks[0];
  const isLfe = c.element.id === LFE;
  if (info.seq !== EIGHT_SHORT) {
    const coeffs = new Float32Array(1024);
    for (let k = 0; k < 1024; k++) coeffs[k] = coef[k] * NORM_LONG;
    const bands = [];
    for (let b = 0; b < M; b++) bands.push({ start: info.swb[b], end: info.swb[b + 1], label: bandLabel(ics.bandType[b], ics.sf[b]) });
    const regions: NonNullable<SpectrumView['regions']> = ics.sections.map((s) => ({ start: info.swb[s.start], end: info.swb[s.end], ...regionOf(s.cb) }));
    if (info.swb[M] < 1024) regions.push({ start: info.swb[M], end: 1024, label: 'Above max_sfb: not stored', kind: 'zero' });
    const top = topFraction(ics);
    const zeros = zeroCount(ics);
    const caption = [
      `${WINDOW_NAMES[info.seq]} window: 1,024 lines in ${info.numSwb} scalefactor bands, ${M} of them stored (max_sfb). Global gain ${ics.globalGain}.`,
      isLfe ? 'The LFE channel carries only deep bass.' : top ? `${pct(zeros / 1024)} of the lines are zero; nothing is coded above ${kHz(top * nyquist)}.` : 'All lines are zero.',
      c.element.msMask ? 'Values are shown after mid/side decoding, as left and right.' : '',
    ].filter(Boolean).join(' ');
    return [{ kind: 'spectrum', title: name, caption, coeffs, nyquist, bands, regions, bits: icsBits }];
  }
  // Eight short windows: one view per window group, with the windows of the
  // group interleaved (line k of window w goes to k · groupLength + w).
  const views: SpectrumView[] = [];
  const G = info.groups.length;
  const shared = icsBits - ics.groupBits.reduce((a, x) => a + x, 0);
  let win = 0;
  for (let g = 0; g < G; g++) {
    const gl = info.groups[g];
    const coeffs = new Float32Array(128 * gl);
    for (let w = 0; w < gl; w++) for (let k = 0; k < 128; k++) coeffs[k * gl + w] = coef[(win + w) * 128 + k] * NORM_SHORT;
    const bands = [];
    for (let b = 0; b < M; b++) bands.push({ start: info.swb[b] * gl, end: info.swb[b + 1] * gl, label: bandLabel(ics.bandType[g * M + b], ics.sf[g * M + b]) });
    const regions: NonNullable<SpectrumView['regions']> = ics.sections
      .filter((s) => s.g === g)
      .map((s) => ({ start: info.swb[s.start] * gl, end: info.swb[s.end] * gl, ...regionOf(s.cb) }));
    if (info.swb[M] < 128) regions.push({ start: info.swb[M] * gl, end: 128 * gl, label: 'Above max_sfb: not stored', kind: 'zero' });
    const wins = gl === 1 ? `window ${win + 1}` : `windows ${win + 1}–${win + gl}`;
    const caption =
      `Eight short windows of 128 lines each, for a sudden change in the sound. Group ${g + 1} of ${G} holds ${wins}. ` +
      (gl > 1 ? `They share one scalefactor per band, which saves bits. Their lines are interleaved: each frequency appears ${gl} times in a row.` : 'It has its own scalefactors.');
    views.push({
      kind: 'spectrum',
      title: `${name} · ${gl === 1 ? `Window ${win + 1}` : `Windows ${win + 1}–${win + gl}`} of 8`,
      caption,
      coeffs,
      nyquist,
      bands,
      regions,
      bits: ics.groupBits[g] + (g === 0 ? shared : 0),
    });
    win += gl;
  }
  return views;
}

function bitViews(blk: RawBlock, bytes: number, prefix: string): BarsView[] {
  const items: BarsView['items'] = [];
  const names = channelNames(blk.elements, false);
  let ch = 0;
  for (const e of blk.elements) {
    const bits = e.end - e.start;
    let label = ELEMENT_NAMES[e.id];
    let note: string | undefined;
    if (e.id === SCE || e.id === LFE) {
      note = names[ch++];
    } else if (e.id === CPE) {
      note = `${names[ch]} + ${names[ch + 1]}`;
      ch += 2;
    } else if (e.id === FIL) {
      label = `FIL · ${extName(e.extType)}`;
      note = `${num(e.bytes ?? 0)} bytes`;
    } else if (e.id === DSE) note = `${num(e.bytes ?? 0)} bytes of private data`;
    else if (e.id === PCE) note = 'Program config: the speaker layout';
    else if (e.id === CCE) note = 'Coupling channel';
    else if (e.id === END) note = 'Marks the end of the frame';
    items.push({ label, value: bits, note });
  }
  const pad = bytes * 8 - (blk.endBit - blk.elements[0].start);
  if (pad > 0) items.push({ label: 'Byte align', value: pad, note: 'Zero bits up to the next byte' });

  // The same bits, by purpose.
  let info = 0, sect = 0, sf = 0, side = 0, spec = 0, sbr = 0, fill = 0, other = 0;
  for (const e of blk.elements) {
    if (e.id === END) {
      info += e.end - e.start;
      continue;
    }
    if (e.id === FIL) {
      if (e.extType === 13 || e.extType === 14) sbr += e.end - e.start;
      else fill += e.end - e.start;
      continue;
    }
    if (!e.ics.length || e.id === CCE) {
      other += e.end - e.start;
      continue;
    }
    let inner = 0;
    for (const ics of e.ics) {
      const m = ics.marks;
      info += m[1] - m[0];
      sect += m[2] - m[1];
      sf += m[3] - m[2];
      side += m[4] - m[3];
      spec += m[5] - m[4];
      inner += m[5] - m[0];
    }
    info += e.end - e.start - inner; // element id, tag, common window, M/S mask
  }
  const purpose: BarsView['items'] = [
    { label: 'Headers', value: info, note: 'Element IDs, global gain, window info, M/S flags, END' },
    { label: 'Sections', value: sect, note: 'Which codebook each band uses' },
    { label: 'Scalefactors', value: sf, note: 'Step size per band' },
    { label: 'TNS + pulses', value: side, note: 'Noise shaping filters' },
    { label: 'Spectral data', value: spec, note: 'The coded frequency lines' },
  ];
  if (sbr) purpose.push({ label: 'SBR', value: sbr, note: 'Data that rebuilds the highs' });
  if (fill) purpose.push({ label: 'Fill', value: fill, note: 'Padding or extra data' });
  if (other) purpose.push({ label: 'Other', value: other });
  return [
    { kind: 'bars', title: `${prefix}Bits per element`, caption: 'A frame is a list of syntax elements that ends with END. Each channel element carries one or two channels.', unit: 'bits', items },
    {
      kind: 'bars',
      title: `${prefix}Where the bits go`,
      caption: 'Most bits should go to the spectral data. Scalefactors and sections are the map that tells the decoder how to read it.',
      unit: 'bits',
      items: purpose.filter((p) => p.value > 0),
    },
  ];
}

function blockColumns(chans: ChannelSpectrum[], t0: number, t1: number, nyquist: number): SpectrumColumn[] {
  if (!chans.length) return [];
  const short = chans.some((c) => c.ics.info.seq === EIGHT_SHORT);
  const n = chans.length;
  if (!short) {
    const mags = new Float32Array(1024);
    for (const c of chans) for (let k = 0; k < 1024; k++) mags[k] += Math.abs(c.coef[k]);
    for (let k = 0; k < 1024; k++) mags[k] *= NORM_LONG / n;
    return [{ t0, t1, mags, nyquist }];
  }
  const out: SpectrumColumn[] = [];
  const dt = (t1 - t0) / 8;
  for (let w = 0; w < 8; w++) {
    const mags = new Float32Array(128);
    for (const c of chans) {
      if (c.ics.info.seq === EIGHT_SHORT) for (let k = 0; k < 128; k++) mags[k] += Math.abs(c.coef[w * 128 + k]) * NORM_SHORT;
      else for (let k = 0; k < 1024; k++) mags[k >> 3] = Math.max(mags[k >> 3], Math.abs(c.coef[k]) * NORM_LONG);
    }
    for (let k = 0; k < 128; k++) mags[k] /= n;
    out.push({ t0: t0 + w * dt, t1: t0 + (w + 1) * dt, mags, nyquist });
  }
  return out;
}

// ---------------------------------------------------------------------------
// File summary

function fileSummary(ctx: Ctx, N: number, nyquist: number, pnsSeen: boolean): { level: 'info' | 'warn'; text: string }[] {
  const step = Math.max(1, Math.floor(N / 2000));
  let frames = 0, short = 0, msBands = 0, cpeBands = 0, isFrames = 0, pnsFrames = 0, zeros = 0, lines = 0, bad = 0, tnsFrames = 0;
  let sbrBytesTotal = 0, bytesTotal = 0;
  const tops: number[] = [];
  for (let i = 0; i < N; i += step) {
    let fd: FrameData;
    try {
      fd = parseFrame(ctx, i);
    } catch {
      bad++;
      continue;
    }
    for (let bi = 0; bi < fd.blocks.length; bi++) {
      const blk = fd.blocks[bi];
      frames++;
      bytesTotal += fd.sizes[bi];
      sbrBytesTotal += sbrBytes(blk);
      let top = 0, pns = false, is = false, tns = false;
      const main = blk.elements.find((e) => e.id === SCE || e.id === CPE);
      if (main?.ics[0].info.seq === EIGHT_SHORT) short++;
      for (const e of blk.elements) {
        if (e.id !== SCE && e.id !== CPE) continue;
        if (e.id === CPE) {
          const [n, t] = msCount(e);
          msBands += n;
          cpeBands += t;
          if (countType(e.ics[1], isIntensity)) is = true;
        }
        for (const ics of e.ics) {
          top = Math.max(top, topFraction(ics), paramTopFraction(ics));
          zeros += zeroCount(ics);
          lines += 1024;
          if (hasNoise(ics)) pns = true;
          if (ics.tns) tns = true;
        }
      }
      if (top > 0) tops.push(top);
      if (pns) pnsFrames++;
      if (is) isFrames++;
      if (tns) tnsFrames++;
    }
  }
  const notes: { level: 'info' | 'warn'; text: string }[] = [];
  if (!frames) return [{ level: 'warn', text: 'No AAC frame could be decoded.' }];
  const approx = step > 1 ? ` (from ${num(frames)} sampled frames)` : '';
  notes.push({
    level: 'info',
    text: short
      ? `${pct(short / frames)} of frames use eight short windows${approx}. The encoder switches to them at sudden attacks, to stop pre-echo.`
      : 'No frame uses short windows. The sound has no sharp attacks, or the encoder chose to ignore them.',
  });
  if (cpeBands) {
    const share = msBands / cpeBands;
    notes.push({
      level: 'info',
      text: share > 0
        ? `Mid/side stereo is on in ${pct(share)} of the stereo bands. The encoder stores L+R and L−R where the channels are alike, because the difference is small and cheap.`
        : 'The encoder never uses mid/side stereo: left and right are always stored separately.',
    });
  }
  if (isFrames) notes.push({ level: 'info', text: `${pct(isFrames / frames)} of frames use intensity stereo. In some high bands the right channel is only a scaled copy of the left, so the stereo image there is narrower.` });
  if (pnsSeen || pnsFrames) notes.push({ level: 'info', text: `${pct(pnsFrames / frames)} of frames use PNS: some noisy bands are sent as an energy only, and the decoder makes up the noise.` });
  if (tnsFrames) notes.push({ level: 'info', text: `${pct(tnsFrames / frames)} of frames use TNS (temporal noise shaping), which moves coding noise under the loud parts of a frame.` });
  if (tops.length) {
    tops.sort((a, b) => a - b);
    const bw = tops[Math.floor(tops.length * 0.95)] * nyquist;
    const cut = bw < nyquist * 0.97;
    notes.push({
      level: !ctx.he && bw < 11000 ? 'warn' : 'info',
      text: ctx.he
        ? `The AAC core codes sound up to about ${kHz(bw)}. SBR rebuilds everything above that, up to ${kHz(nyquist * 2)}.`
        : cut
          ? `Almost nothing is coded above ${kHz(bw)}. The encoder cuts the highs to spend its bits where the ear is more sensitive.`
          : `The encoder codes sound up to the Nyquist limit of ${kHz(nyquist)}.`,
    });
  }
  if (lines) notes.push({ level: 'info', text: `${pct(zeros / lines)} of all stored frequency lines are zero. Zeros are what make AAC small: whole zero sections cost no bits.` });
  if (ctx.he && bytesTotal) notes.push({ level: 'info', text: `SBR data takes ${pct(sbrBytesTotal / bytesTotal)} of the audio bytes, yet it rebuilds the whole upper half of the spectrum.` });
  if (bad) notes.push({ level: 'warn', text: `${num(bad)} ${bad === 1 ? 'frame' : 'frames'} could not be decoded${approx}.` });
  return notes;
}
